import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import * as d3 from 'd3';
import { Share2, Sparkles, Loader2 } from 'lucide-react';
import { engagementsApi, findingsApi, aiApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { buildAttackGraph, type AttackNode, type AttackEdge } from '../lib/attack-path';
import { useTheme } from '../lib/theme';
import { SEVERITY_COLOR_BY_THEME } from '../lib/chart-colors';
import type { FindingRecord, Severity } from '../lib/types';

/**
 * Phase 4 — attack path visualizer. Same "colour-coded by severity" ask as the original spec, but
 * see lib/attack-path.ts's top docstring for why this draws one edge per finding toward a fixed
 * goal node rather than computing a real graph-traversal path — that's a deliberately scoped-out,
 * dedicated analysis feature, not something this page fakes.
 */

/** Node-type colours (account/host/goal) are a dataviz-only distinction, not part of the
 * severity/status system above, so they intentionally stay fixed across themes. */
const NODE_COLOR: Record<AttackNode['kind'], string> = {
  account: '#2563eb',
  host: '#0891b2',
  goal: '#7c2d12',
};

interface SimNode extends AttackNode, d3.SimulationNodeDatum {}
interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  id: string;
  technique: string;
  severity: Severity;
}

function AttackGraphSvg({ nodes, edges }: { nodes: AttackNode[]; edges: AttackEdge[] }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { resolved } = useTheme();
  const severityColor = SEVERITY_COLOR_BY_THEME[resolved];
  // Separates overlapping nodes/edges with a ring matching the canvas, not a hardcoded white that
  // would look like a stray highlight against a dark canvas.
  const nodeRingColor = resolved === 'dark' ? '#10161e' : '#ffffff';

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || nodes.length === 0) return;

    const width = svg.clientWidth || 800;
    const height = 520;

    const simNodes: SimNode[] = nodes.map((n) => ({ ...n }));
    const nodeById = new Map(simNodes.map((n) => [n.id, n]));
    const simLinks: SimLink[] = edges
      .filter((e) => nodeById.has(e.source) && nodeById.has(e.target))
      .map((e) => ({ id: e.id, source: e.source, target: e.target, technique: e.technique, severity: e.severity }));

    const selection = d3.select(svg);
    selection.selectAll('*').remove();
    selection.attr('viewBox', `0 0 ${width} ${height}`);

    const simulation = d3
      .forceSimulation(simNodes)
      .force(
        'link',
        d3
          .forceLink<SimNode, SimLink>(simLinks)
          .id((d) => d.id)
          .distance(140),
      )
      .force('charge', d3.forceManyBody().strength(-260))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collide', d3.forceCollide(36));

    const link = selection
      .append('g')
      .selectAll('line')
      .data(simLinks)
      .join('line')
      .attr('stroke', (d) => severityColor[d.severity])
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.8);

    link.append('title').text((d) => d.technique);

    const node = selection
      .append('g')
      .selectAll<SVGCircleElement, SimNode>('circle')
      .data(simNodes)
      .join('circle')
      .attr('r', (d) => (d.kind === 'goal' ? 22 : 14))
      .attr('fill', (d) => NODE_COLOR[d.kind])
      .attr('stroke', nodeRingColor)
      .attr('stroke-width', 1.5)
      .call(
        d3
          .drag<SVGCircleElement, SimNode>()
          .on('start', (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on('drag', (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on('end', (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }),
      );

    node.append('title').text((d) => d.label);

    const label = selection
      .append('g')
      .selectAll('text')
      .data(simNodes)
      .join('text')
      .text((d) => d.label)
      .attr('font-size', 11)
      .attr('dx', 18)
      .attr('dy', 4)
      .attr('fill', 'currentColor');

    simulation.on('tick', () => {
      link
        .attr('x1', (d) => (d.source as SimNode).x ?? 0)
        .attr('y1', (d) => (d.source as SimNode).y ?? 0)
        .attr('x2', (d) => (d.target as SimNode).x ?? 0)
        .attr('y2', (d) => (d.target as SimNode).y ?? 0);
      node.attr('cx', (d) => d.x ?? 0).attr('cy', (d) => d.y ?? 0);
      label.attr('x', (d) => d.x ?? 0).attr('y', (d) => d.y ?? 0);
    });

    return () => {
      simulation.stop();
    };
  }, [nodes, edges, severityColor, nodeRingColor]);

  return <svg ref={svgRef} role="img" aria-label="Attack path graph" className="w-full text-ink" style={{ height: 520 }} />;
}

function Legend() {
  const { resolved } = useTheme();
  const severityColor = SEVERITY_COLOR_BY_THEME[resolved];
  return (
    <div className="flex flex-wrap gap-4 text-xs text-ink-muted">
      {(Object.keys(severityColor) as Severity[]).map((severity) => (
        <span key={severity} className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: severityColor[severity] }} />
          {severity}
        </span>
      ))}
    </div>
  );
}

/** AI Attack Narrative — on-demand, see AttackPaths() below. This page aggregates findings across
 * every engagement (no single "current run" the way RunDetail.tsx has, and endpoint-agent findings
 * have no runId at all), so this calls the standalone POST /v1/ai/attack-path-narrative with the
 * graphed findings' own data instead of POST /v1/runs/:id/attack-path-narrative. */
function AttackNarrativeCard({
  findings,
  targetName,
}: {
  findings: Array<{ title: string; severity: string; description: string; affectedAsset: string }>;
  targetName: string;
}) {
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: () => aiApi.attackPathNarrative({ findings, targetName }),
    onError: (err) => toast({ title: 'Could not generate narrative', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles size={18} className="text-brand" />
          AI Attack Narrative
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-ink">
        {!mutation.data && !mutation.isPending && !mutation.isError ? (
          <div className="space-y-2">
            <p className="text-ink-muted">Generate a narrative connecting these findings into a realistic attack scenario.</p>
            <Button size="sm" variant="outline" onClick={() => mutation.mutate()}>
              <Sparkles size={14} />
              Generate narrative
            </Button>
          </div>
        ) : mutation.isPending ? (
          <Button size="sm" variant="outline" disabled>
            <Loader2 size={14} className="animate-spin" />
            Generating...
          </Button>
        ) : mutation.isError || (mutation.data && 'error' in mutation.data) ? (
          <div className="space-y-2">
            <p className="text-ink-muted">Could not generate narrative. Try again.</p>
            <Button size="sm" variant="outline" onClick={() => mutation.mutate()}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="whitespace-pre-line text-ink">{mutation.data!.narrative}</p>
            <p className="text-xs text-ink-faint">Powered by DefendIQ AI</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AttackPaths() {
  const engagementsQuery = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list() });
  const engagements = engagementsQuery.data?.engagements ?? [];

  const findingQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['findings', e.id],
      queryFn: () => findingsApi.forEngagement(e.id),
      enabled: engagements.length > 0,
    })),
  });
  const tenantFindingsQuery = useQuery({ queryKey: ['findings', 'tenant-wide'], queryFn: () => findingsApi.listForTenant() });
  const aiCapabilitiesQuery = useQuery({ queryKey: ['ai-capabilities'], queryFn: () => aiApi.capabilities() });
  const aiEnabled = aiCapabilitiesQuery.data?.enabled ?? false;

  const isLoading = engagementsQuery.isLoading || tenantFindingsQuery.isLoading;
  const isError = engagementsQuery.isError || tenantFindingsQuery.isError;

  const allFindings: FindingRecord[] = useMemo(() => {
    const byId = new Map<string, FindingRecord>();
    for (const q of findingQueries) {
      for (const f of Object.values(q.data?.byZone ?? {}).flat()) byId.set(f.id, f);
    }
    for (const f of tenantFindingsQuery.data?.findings ?? []) byId.set(f.id, f);
    return [...byId.values()];
  }, [findingQueries, tenantFindingsQuery.data]);

  const graph = useMemo(() => buildAttackGraph(allFindings), [allFindings]);

  // The graphed findings' own data, in the shape POST /v1/ai/attack-path-narrative wants — this
  // page has no single "current run" (it aggregates across every engagement) and endpoint-agent
  // findings have no runId at all, so the narrative is generated straight from this data rather
  // than a run lookup. Correlates back through each graphed edge's findingId since AttackEdge
  // itself only carries technique/severity/findingId, not the full finding.
  const graphFindings = useMemo(() => {
    const findingsById = new Map(allFindings.map((f) => [f.id, f]));
    return graph.edges
      .map((e) => findingsById.get(e.findingId))
      .filter((f): f is FindingRecord => Boolean(f))
      .map((f) => ({ title: f.title, severity: f.severity, description: f.description, affectedAsset: f.target }));
  }, [allFindings, graph.edges]);

  const targetName = useMemo(() => {
    const assets = [...new Set(graphFindings.map((f) => f.affectedAsset))];
    return assets.length > 0 ? assets.join(', ') : 'the tested environment';
  }, [graphFindings]);

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={engagementsQuery.error ?? tenantFindingsQuery.error} onRetry={() => engagementsQuery.refetch()} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Attack Paths"
        description="Visualizes AD and endpoint red-team findings as attack techniques pointing toward a likely impact — derived from findings already collected, not a computed graph traversal."
      />

      {aiEnabled && graphFindings.length > 0 ? <AttackNarrativeCard findings={graphFindings} targetName={targetName} /> : null}

      <Card>
        <CardContent className="pt-6">
          {graph.nodes.length === 0 ? (
            <EmptyState
              icon={<Share2 size={32} />}
              title="No attack-path findings yet"
              description="Run AD enumeration (ldapdomaindump, enum4linux, kerbrute, BloodHound) or endpoint red-team checks to populate this view."
            />
          ) : (
            <div className="space-y-4">
              <Legend />
              <div className="rounded-xl border border-border">
                <AttackGraphSvg nodes={graph.nodes} edges={graph.edges} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
